/**
 * User-facing notifications. Thin wrapper over react-hot-toast so we can
 * swap the underlying library later without touching every call site.
 *
 * Usage:
 *   import { notifyError, notifySuccess } from '../services/notify';
 *   try { ... } catch (e) { notifyError('Failed to save grade'); }
 */

import toast from 'react-hot-toast';

/** Show a red error toast. Pass a short, user-facing message — not a stack trace. */
export const notifyError = (message: string, options?: { id?: string; duration?: number }) => {
  toast.error(message, {
    id: options?.id,                      // pass an id to dedupe rapid repeats
    duration: options?.duration ?? 5000,  // errors stay longer by default
  });
};

/** Show a green success toast. */
export const notifySuccess = (message: string, options?: { id?: string; duration?: number }) => {
  toast.success(message, {
    id: options?.id,
    duration: options?.duration ?? 3000,
  });
};

/**
 * Show a WARNING toast: something partly succeeded and needs the reader's attention.
 *
 * Distinct from `notifyError` because a red X above text that begins "Report cards released…" reads as
 * "the release failed", which invites an admin to repeat an action that already worked. Longer by
 * default too: these messages carry an instruction, and 5s of a two-sentence toast is below reading
 * speed for most people.
 */
export const notifyWarning = (message: string, options?: { id?: string; duration?: number }) => {
  toast(message, {
    id: options?.id,
    icon: '⚠️',
    duration: options?.duration ?? 12000,
  });
};

/** Show a neutral info toast (no colored icon, just text). */
export const notifyInfo = (message: string, options?: { id?: string; duration?: number }) => {
  toast(message, {
    id: options?.id,
    duration: options?.duration ?? 3000,
  });
};
